import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import axios from '../../services/axios';
import toast from 'react-hot-toast';

const HourEntryForm = ({ internId, entry, onSuccess, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [templates, setTemplates] = useState([]);

    const { register, handleSubmit, setValue, formState: { errors } } = useForm({
        defaultValues: entry ? {
            template_id: entry.template_id,
            category_id: entry.category_id,
            date: entry.date,
            hours: entry.hours,
            activity_type: entry.activity_type,
            description: entry.description,
            evidence_url: entry.evidence_url || '',
            evidence_type: entry.evidence_type || ''
        } : undefined
    });

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (!entry) return;
        setValue('template_id', entry.template_id);
        setValue('category_id', entry.category_id);
        setValue('date', entry.date);
        setValue('hours', entry.hours);
        setValue('activity_type', entry.activity_type);
        setValue('description', entry.description || '');
        setValue('evidence_url', entry.evidence_url || '');
        setValue('evidence_type', entry.evidence_type || '');
    }, [entry, setValue]);

    const loadData = async () => {
        try {
            const [categoriesRes, templatesRes] = await Promise.all([
                axios.get('/hours/categories'),
                axios.get('/hours/templates')
            ]);
            setCategories(categoriesRes.data.data);
            setTemplates(templatesRes.data.data);

            if (!entry && templatesRes.data.data.length > 0) {
                const preferred = templatesRes.data.data.find((t) => t.is_default) || templatesRes.data.data[0];
                setValue('template_id', preferred.id);
            }
        } catch (error) {
            toast.error('Failed to load form data');
        }
    };

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            if (entry?.id) {
                await axios.put(`/hours/${entry.id}`, data);
                toast.success('Hour entry updated');
            } else {
                const endpoint = internId ? `/hours/${internId}` : '/hours';
                await axios.post(endpoint, data);
                toast.success('Hour entry saved as draft');
            }
            onSuccess?.();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save hour entry');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Template</label>
                    <select
                        {...register('template_id', { required: 'Template is required' })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                    >
                        <option value="">Select template</option>
                        {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                                {template.name} ({template.total_hours} hours)
                            </option>
                        ))}
                    </select>
                    {errors.template_id && <p className="mt-1 text-sm text-red-600">{errors.template_id.message}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Category</label>
                    <select
                        {...register('category_id', { required: 'Category is required' })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                    >
                        <option value="">Select category</option>
                        {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                                {category.display_name}
                            </option>
                        ))}
                    </select>
                    {errors.category_id && <p className="mt-1 text-sm text-red-600">{errors.category_id.message}</p>}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Date</label>
                    <input
                        type="date"
                        {...register('date', { required: 'Date is required' })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                    />
                    {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Hours</label>
                    <input
                        type="number"
                        step="0.5"
                        min="0.5"
                        max="24"
                        {...register('hours', {
                            required: 'Hours are required',
                            min: { value: 0.5, message: 'Minimum 0.5 hours' },
                            max: { value: 24, message: 'Maximum 24 hours per day' }
                        })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="e.g., 2.5"
                    />
                    {errors.hours && <p className="mt-1 text-sm text-red-600">{errors.hours.message}</p>}
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Activity Type</label>
                <select
                    {...register('activity_type', { required: 'Activity type is required' })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                >
                    <option value="">Select activity type</option>
                    <option value="training">Training</option>
                    <option value="business_discovery">Business Discovery</option>
                    <option value="assessment">Assessment</option>
                    <option value="process_mapping">Process Mapping</option>
                    <option value="solution_training">Solution Training</option>
                    <option value="project_work">Project Work</option>
                    <option value="pilot_support">Pilot Support</option>
                    <option value="meeting">Meeting</option>
                    <option value="presentation">Presentation</option>
                    <option value="reflection">Reflection</option>
                    <option value="other">Other</option>
                </select>
                {errors.activity_type && <p className="mt-1 text-sm text-red-600">{errors.activity_type.message}</p>}
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                    {...register('description', { required: 'Description is required' })}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                    placeholder="Describe what you did and what you learned..."
                />
                {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>}
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Evidence URL (required to submit)</label>
                <input
                    type="url"
                    {...register('evidence_url')}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black min-h-[44px]"
                    placeholder="https://drive.google.com/..."
                />
                <p className="text-xs text-gray-500 mt-1">You can save a draft without evidence, but evidence is required before submission.</p>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Evidence Type</label>
                <select
                    {...register('evidence_type')}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                >
                    <option value="">Select evidence type</option>
                    <option value="pdf">PDF</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="link">Link</option>
                    <option value="document">Document</option>
                    <option value="other">Other</option>
                </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                {onCancel && (
                    <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
                        Cancel
                    </button>
                )}
                <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                    {loading ? 'Saving...' : (entry ? 'Save updates' : 'Save draft')}
                </button>
            </div>
        </form>
    );
};

export default HourEntryForm;
